import { mm } from "@inovitas/infra3dsdk";
import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import { defaultStore } from "shared/common/stores/defaultStore";
import AxisscaleBar from "./AxisscaleBar";
import AxisscaleSelect from "./AxisscaleSelect";

export default function Axisscale() {
  const viewer = defaultStore((state) => state.viewer);
  const showAxisscale = defaultStore((state) => state.showAxisscale);
  const changeShowAxisscale = defaultStore(
    (state) => state.changeShowAxisscale
  );
  const axisCollection = defaultStore((state) => state.axisCollection);

  const { showMessage } = useSnackbar();

  const { t } = useTranslation();

  const [activeAxis, setActiveAxis] = useState<mm.Axis | null>(null);
  const activeAxisName = useRef<string | null>(null);

  const onNavigationAxisChanged = (axis: mm.Axis | null) => {
    if (!viewer?.getScene().getLRHandler().reloadingAxis) {
      setActiveAxis(axis);
    }

    if (axis) {
      changeShowAxisscale(true);
      if (activeAxisName.current && activeAxisName.current !== axis.name)
        showMessage(t("alert_info.km_axis_changed"), "info");
      return;
    }

    if (!viewer?.getScene().getLRHandler().reloadingAxis) {
      changeShowAxisscale(false);
    }
  };

  const onLayerAdded = (layer: mm.Layer) => {
    if (layer.type === mm.LAYERTYPE.NETWORKLAYER) {
      setTimeout(() => {
        // Do not show alert, if no image has been loaded (yet)
        if (!viewer?.getScene().getCurrentGeoframe()) return;
        // Always read the latest value from the ref, which is kept in sync with the store
        if (defaultStore.getState().showAxisscale === undefined) {
          changeShowAxisscale(false);
        }
      }, 7000);
    }
  };

  useEffect(() => {
    if (!viewer) return;

    viewer.on("navigationaxischanged", onNavigationAxisChanged);
    viewer.on("layeradded", onLayerAdded);

    return () => {
      viewer.off("navigationaxischanged", onNavigationAxisChanged);
      viewer.off("layeradded", onLayerAdded);
    };
  }, [viewer]);

  useEffect(() => {
    if (activeAxis) {
      activeAxisName.current = activeAxis.name;
    } else {
      activeAxisName.current = null;
    }
  }, [activeAxis]);

  useEffect(() => {
    if (!viewer) return; // To suppress the first mount
    if (!showAxisscale) {
      showMessage(t("alert_info.axisscale_bar_hide"), "info");
    }
  }, [showAxisscale]);

  const handleActiveAxisChange = (value: string) => {
    const matchedAxis = axisCollection.find((axis) => axis.name === value);
    if (!matchedAxis) return;
    viewer?.getScene().getLRHandler().setActiveNavigationAxis(matchedAxis.axis);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          maxWidth: !showAxisscale ? 0 : "800px",
          opacity: !showAxisscale ? 0 : 1,
          transition: "max-width 0.5s",
          overflow: !showAxisscale ? "hidden" : "visible",
        }}
      >
        {showAxisscale ? (
          // Axisscalebar should not be displayed when the axis is null
          // it would leed to unnecessary computation
          <AxisscaleBar activeAxis={activeAxis} />
        ) : (
          // Placeholder for the AxisscaleBar
          <Box
            sx={{
              width: { xs: "150px", md: "300px", lg: "600px" },
              transition: "width 0.3s",
              height: "40px",
              paddingInlineStart: 1,
              paddingInlineEnd: 2,
            }}
          ></Box>
        )}
        <AxisscaleSelect
          allAxis={axisCollection}
          onChange={handleActiveAxisChange}
          activeAxis={activeAxis}
        />
      </Box>
    </>
  );
}
